/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useEffect, useMemo, useRef, useCallback} from 'react'
import axios from 'axios'
import {API_URL} from '../../modules/auth/core/_requests'
import {useAuth} from '../../modules/auth'
import {KangerWholesaleUpdateModal} from './KangerWholesaleUpdateModal'
import {KangerSkuSyncModal} from './KangerSkuSyncModal'
import { KangerWholesaleSingleProductModal } from './KangerWholesaleSingleProductModal'

// Define the interface based on the backend structure
interface KangerInventoryItem {
    product_id: number;
    product_title: string;
    product_url: string;
    option_name?: string;
    option_id?: number;
    variant_name: string;
    attribute_id: number;
    variant_id?: number;
    stock?: number;
    is_in_stock?: boolean;
    price_formatted?: string;
    price_value?: number;
    out_of_stock_message?: string;
    parent_image_url?: string;
    variant_image_url?: string;
    scrape_status: string;
    error_message?: string;
    last_scraped_at: Date;
    brand_name?: string;
    // New Shopify connection fields
    shopify_product_id?: number;
    shopify_variant_id?: number;
    is_connected_to_shopify?: boolean;
    shopify_store_name?: string;
    connected_at?: Date;
    last_sync_at?: Date;
}

// Interface for grouped data
interface GroupedProduct {
  productId: number
  productTitle: string
  productUrl: string
  parentImageUrl?: string
  variants: KangerInventoryItem[]
  isConnectedToShopify: boolean
  shopifyProductId?: number
  connectionStatus?: 'never_connected' | 'partial' | 'disconnected' | 'connected'
  connectedVariantsCount?: number
}

// Interface for toggle states per product (simplified to single store)
interface ProductToggleStates {
  [productId: number]: boolean;
}

// Interface for variant selection in modal
interface VariantSelection {
  [attributeId: number]: boolean;
}

// Interface for variant management
interface VariantConnectionStatus {
  [attributeId: number]: boolean;
}

// Props interface for the main page component
interface KangerWholesalePageProps {
  onUpdateInventory: () => void;
  isUpdatingInventory: boolean;
  lastUpdateTime: Date | null;
  refreshData: () => void;
  refreshTrigger: number;
}

export const KangerWholesalePage: FC<KangerWholesalePageProps> = ({
  onUpdateInventory,
  isUpdatingInventory,
  lastUpdateTime,
  refreshData,
  refreshTrigger
}) => {
  const {currentUser} = useAuth()
  const currentStoreName = currentUser?.decoded?.current_shopify_store || ''

  // Helper function to format time ago
  const formatTimeAgo = (date: Date | string | number | undefined): string => {
    if (!date) return '';

    const now = new Date();
    const syncDate = typeof date === 'number' ? new Date(date) : new Date(date);
    const diffMs = now.getTime() - syncDate.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  }

  const [inventoryData, setInventoryData] = useState<GroupedProduct[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedBrand, setSelectedBrand] = useState<string>('') // Legacy, to be removed or adapted
  const [brands, setBrands] = useState<string[]>([])
  const [toggleStates, setToggleStates] = useState<ProductToggleStates>({})

  // --- Pagination & Server-side Filtering State ---
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [totalProducts, setTotalProducts] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(150) // Products per page
  
  // --- State for connected count ---
  const [connectedCount, setConnectedCount] = useState<number | null>(null);
  const [isCountLoading, setIsCountLoading] = useState<boolean>(false);

  // Modal states
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedProduct, setSelectedProduct] = useState<GroupedProduct | null>(null)
  const [variantSelections, setVariantSelections] = useState<VariantSelection>({})
  const [modalLoading, setModalLoading] = useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [showAIModal, setShowAIModal] = useState<boolean>(false)
  const [showFinalModal, setShowFinalModal] = useState<boolean>(false)
  const [showVariantManagementModal, setShowVariantManagementModal] = useState<boolean>(false)
  const [variantConnectionStatus, setVariantConnectionStatus] = useState<VariantConnectionStatus>({})
  
  // Filter states
  const [connectionFilter, setConnectionFilter] = useState<'all' | 'connected' | 'disconnected'>('all')
  const [brandFilter, setBrandFilter] = useState<string>('')
  
  // AI processing states
  const [seoKeywords, setSeoKeywords] = useState<string>('')
  const [additionalPrompt, setAdditionalPrompt] = useState<string>('')
  const [optimizedTitle, setOptimizedTitle] = useState<string>('')
  const [optimizedDescription, setOptimizedDescription] = useState<string>('')
  const [aiProcessing, setAiProcessing] = useState<boolean>(false)

  const startProduct = totalProducts > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endProduct = Math.min(currentPage * pageSize, totalProducts);

  // Debounce search term to avoid excessive API calls
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);
    return debouncedValue;
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedBrandFilter = useDebounce(brandFilter, 500);

  const fetchInventory = useCallback(async () => {
    setLoading(true)
    setError(null)
    setConnectedCount(null); // Reset count on new filter/page
    setIsCountLoading(true);

    // --- Fire off connected count request in parallel ---
    const fetchCount = async () => {
      try {
        const countParams = {
          searchTerm: debouncedSearchTerm,
          brandFilter: debouncedBrandFilter,
          storeName: currentStoreName,
        };
        const countResponse = await axios.get(`${API_URL}/api/kangerwholesale/get-connected-count`, { params: countParams });
        setConnectedCount(countResponse.data.connectedProducts);
      } catch (countError) {
        console.error("Failed to fetch connected count", countError);
        setConnectedCount(0); // Default to 0 on error
      } finally {
        setIsCountLoading(false);
      }
    };
    fetchCount();
    // ----------------------------------------------------

    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        searchTerm: debouncedSearchTerm,
        brandFilter: debouncedBrandFilter,
        connectionFilter,
        storeName: currentStoreName,
      };

      const response = await axios.get(`${API_URL}/api/kangerwholesale/get-inventory`, { params });
      
      const { items, totalProducts, totalPages } = response.data.data;

      // Group variants by product_id (logic remains the same, but now on paged data)
      const grouped = items.reduce((acc: Map<number, GroupedProduct>, item: KangerInventoryItem) => {
        const existing = acc.get(item.product_id);
        if (existing) {
          existing.variants.push(item);
        } else {
          const isConnected = !!(item.is_connected_to_shopify && item.shopify_store_name === currentStoreName);
          acc.set(item.product_id, {
            productId: item.product_id,
            productTitle: item.product_title,
            productUrl: item.product_url,
            parentImageUrl: item.parent_image_url,
            variants: [item],
            isConnectedToShopify: isConnected,
            shopifyProductId: item.shopify_product_id,
          });
        }
        return acc;
      }, new Map<number, GroupedProduct>());

      // Update connection status for each grouped product
      grouped.forEach((product: GroupedProduct) => {
        const connectedVariants = product.variants.filter(v => 
          v.is_connected_to_shopify && v.shopify_store_name === currentStoreName
        );
        const hasShopifyId = product.variants.some(v => v.shopify_product_id);
        
        product.isConnectedToShopify = connectedVariants.length > 0;
        product.shopifyProductId = connectedVariants[0]?.shopify_product_id;
        product.connectedVariantsCount = connectedVariants.length;
        
        if (!hasShopifyId) {
          product.connectionStatus = 'never_connected';
        } else if (connectedVariants.length === 0) {
          product.connectionStatus = 'disconnected';
        } else if (connectedVariants.length === product.variants.length) {
          product.connectionStatus = 'connected';
        } else {
          product.connectionStatus = 'partial';
        }
      });

      const allProducts: GroupedProduct[] = Array.from(grouped.values())
      setInventoryData(allProducts)
      setTotalProducts(totalProducts)
      setTotalPages(totalPages)

      // Initialize toggle states based on connection status for the current page
      const initialToggles: ProductToggleStates = {};
      allProducts.forEach((product: GroupedProduct) => {
        initialToggles[product.productId] = product.isConnectedToShopify;
      });
      setToggleStates(initialToggles);

    } catch (err) {
      console.error('Error fetching KangerWholesale inventory:', err)
      setError('Failed to fetch inventory data. Please try again later.')
    } finally {
      setLoading(false)
    }
  }, [currentPage, pageSize, debouncedSearchTerm, debouncedBrandFilter, connectionFilter, currentStoreName, refreshTrigger]);

  // Effect to fetch brands (only once)
  useEffect(() => {
    const fetchAllBrands = async () => {
       try {
        // This endpoint would need to be created to efficiently get all brands
        // For now, we'll keep the old logic but it will only show brands on the current page.
        // A proper implementation would have GET /api/kangerwholesale/brands
       } catch (error) {
         console.error("Could not fetch brands", error)
       }
    };
    fetchAllBrands();
  }, [])

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, debouncedBrandFilter, connectionFilter]);

  // Main data fetching effect
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);


  // Filtered data based on search term, selected brand, and connection status
  // THIS IS NOW REMOVED, as filtering is done on the server. We directly use `inventoryData`.
  // const filteredInventory = useMemo(() => { ... });

  // Handler for changing toggle state
  const handleToggleChange = (productId: number) => {
    const product = inventoryData.find(p => p.productId === productId);
    if (!product) return;

    const newToggleState = !toggleStates[productId];
    
    if (newToggleState) {
      // Turning ON - show modal
      setSelectedProduct(product);
      // Initialize variant selections - all variants selected by default when turning on
      const initialSelections: VariantSelection = {};
      product.variants.forEach(variant => {
        initialSelections[variant.attribute_id] = true;
      });
      setVariantSelections(initialSelections);
      setShowModal(true);
    } else {
      // Turning OFF - disconnect immediately
      handleDisconnectProduct(productId);
    }
  };

  const handleDisconnectProduct = async (productId: number) => {
    try {
      await axios.post(`${API_URL}/api/kangerwholesale/disconnect-product`, {
        productId,
        storeName: currentStoreName
      });
      
      // Update local state
      setToggleStates(prev => ({
        ...prev,
        [productId]: false
      }));
      
      // Update inventory data
      setInventoryData(prev => prev.map(product => 
        product.productId === productId 
          ? { ...product, isConnectedToShopify: false, shopifyProductId: undefined }
          : product
      ));
      
      console.log(`Disconnected product ${productId} from ${currentStoreName}`);
    } catch (error) {
      console.error('Error disconnecting product:', error);
      alert('Failed to disconnect product. Please try again.');
    }
  };

  const handleVariantSelectionChange = (attributeId: number) => {
    setVariantSelections(prev => ({
      ...prev,
      [attributeId]: !prev[attributeId]
    }));
  };

  const handleVariantConnectionChange = (attributeId: number) => {
    setVariantConnectionStatus(prev => ({
      ...prev,
      [attributeId]: !prev[attributeId]
    }));
  };

  const handleConnectCreate = async () => {
    if (!selectedProduct) return;
    
    setModalLoading(true);
    try {
      // First check if product exists in Shopify by SKU or title
      const checkResponse = await axios.post(`${API_URL}/api/kangerwholesale/check-shopify-product`, {
        productTitle: selectedProduct.productTitle,
        storeName: currentStoreName,
        variants: selectedProduct.variants.filter(v => variantSelections[v.attribute_id])
      });

      if (checkResponse.data.exists) {
        // Product exists, connect it
        await handleConnectExistingProduct(checkResponse.data.product);
      } else {
        // Product doesn't exist, show AI enhancement modal
        setShowCreateModal(false);
        setShowAIModal(true);
      }
    } catch (error) {
      console.error('Error checking/connecting product:', error);
      alert('Failed to check product. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleConnectExistingProduct = async (shopifyProduct: any) => {
    if (!selectedProduct) return;

    try {
      const shopifyProductId = parseInt(shopifyProduct.id.replace('gid://shopify/Product/', ''));

      // Create a map of Shopify variants by SKU for easy lookup
      const shopifyVariantMap = new Map<string, any>();
      shopifyProduct.variants.edges.forEach(({ node }: { node: any }) => {
        if (node.sku) {
          shopifyVariantMap.set(node.sku, node);
        }
      });

      // Enrich local variants with Shopify variant ID
      const enrichedVariants = selectedProduct.variants
        .filter(v => variantSelections[v.attribute_id])
        .map(localVariant => {
          const sku = `KANGER-${localVariant.product_id}-${localVariant.attribute_id}`;
          const shopifyVariant = shopifyVariantMap.get(sku);
          return {
            ...localVariant,
            shopify_variant_id: shopifyVariant ? parseInt(shopifyVariant.id.replace('gid://shopify/ProductVariant/', '')) : null
          };
        });
      
      // Check for variants that couldn't be matched
      const unmatchedVariants = enrichedVariants.filter(v => !v.shopify_variant_id);
      if (unmatchedVariants.length > 0) {
        const unmatchedNames = unmatchedVariants.map(v => v.variant_name).join(', ');
        alert(`Warning: Could not find matching Shopify variants for the following local variants based on SKU: ${unmatchedNames}. These will not be connected.`);
      }

      const variantsToConnect = enrichedVariants.filter(v => v.shopify_variant_id);

      if (variantsToConnect.length === 0) {
        alert('No matching variants found to connect. Please check the SKUs in your Shopify store.');
        return;
      }
      
      await axios.post(`${API_URL}/api/kangerwholesale/connect-existing-product`, {
        productId: selectedProduct.productId,
        shopifyProductId,
        storeName: currentStoreName,
        variants: variantsToConnect
      });

      // Update local state
      setToggleStates(prev => ({
        ...prev,
        [selectedProduct.productId]: true
      }));

      setInventoryData(prev => prev.map(product => 
        product.productId === selectedProduct.productId 
          ? { ...product, isConnectedToShopify: true, shopifyProductId }
          : product
      ));

      setShowModal(false);
      setSelectedProduct(null);
      alert('Product connected successfully!');
    } catch (error) {
      console.error('Error connecting existing product:', error);
      alert('Failed to connect product. Please try again.');
    }
  };

  const handleProcessWithAI = async () => {
    if (!selectedProduct) return;
    
    setAiProcessing(true);
    try {
      const selectedVariants = selectedProduct.variants.filter(v => variantSelections[v.attribute_id]);
      
      const response = await axios.post(`${API_URL}/api/kangerwholesale/process-with-ai`, {
        productTitle: selectedProduct.productTitle,
        variants: selectedVariants,
        seoKeywords,
        additionalPrompt
      });

      setOptimizedTitle(response.data.optimizedTitle);
      setOptimizedDescription(response.data.optimizedDescription);
      setShowAIModal(false);
      setShowFinalModal(true);
    } catch (error) {
      console.error('Error processing with AI:', error);
      alert('Failed to process with AI. Please try again.');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleCreateNewProduct = async () => {
    if (!selectedProduct) return;

    setModalLoading(true);
    try {
      const selectedVariants = selectedProduct.variants.filter(v => variantSelections[v.attribute_id]);
      
      // Use enhanced creation endpoint with AI-optimized content
      const createResponse = await axios.post(`${API_URL}/api/kangerwholesale/create-shopify-product-enhanced`, {
        productTitle: selectedProduct.productTitle,
        optimizedTitle,
        optimizedDescription,
        storeName: currentStoreName,
        variants: selectedVariants,
        parentImageUrl: selectedProduct.parentImageUrl,
        productId: selectedProduct.productId
      });

      const shopifyProductId = createResponse.data.shopifyProductId;

      // Update local state
      setToggleStates(prev => ({
        ...prev,
        [selectedProduct.productId]: true
      }));

      setInventoryData(prev => prev.map(product => 
        product.productId === selectedProduct.productId 
          ? { ...product, isConnectedToShopify: true, shopifyProductId }
          : product
      ));

      closeModal();
      alert(`Product created successfully! View in Shopify: ${createResponse.data.shopifyUrl}`);
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Failed to create product. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleManageVariants = async (product: GroupedProduct) => {
    setSelectedProduct(product);
    
    try {
      // Fetch current connection status for variants
      const response = await axios.get(`${API_URL}/api/kangerwholesale/connection-status/${product.productId}/${currentStoreName}`);
      
      // Initialize variant connection status
      const initialStatus: VariantConnectionStatus = {};
      response.data.variants.forEach((variant: any) => {
        initialStatus[variant.attribute_id] = variant.is_connected_to_shopify || false;
      });
      setVariantConnectionStatus(initialStatus);
      setShowVariantManagementModal(true);
    } catch (error) {
      console.error('Error fetching variant status:', error);
      alert('Failed to load variant status. Please try again.');
    }
  };

  const handleSaveVariantConnections = async () => {
    if (!selectedProduct) return;

    setModalLoading(true);
    try {
      // Prepare variant updates
      const variantUpdates = Object.entries(variantConnectionStatus).map(([attributeId, isConnected]) => ({
        attributeId: parseInt(attributeId),
        isConnected
      }));

      await axios.post(`${API_URL}/api/kangerwholesale/manage-variants`, {
        productId: selectedProduct.productId,
        storeName: currentStoreName,
        variantUpdates
      });

      // Update local state
      const connectedCount = Object.values(variantConnectionStatus).filter(Boolean).length;
      const hasAnyConnected = connectedCount > 0;
      
      setToggleStates(prev => ({
        ...prev,
        [selectedProduct.productId]: hasAnyConnected
      }));

      setInventoryData(prev => prev.map(product => 
        product.productId === selectedProduct.productId 
          ? { 
              ...product, 
              isConnectedToShopify: hasAnyConnected,
              connectedVariantsCount: connectedCount,
              connectionStatus: connectedCount === 0 ? 'disconnected' : 
                               connectedCount === product.variants.length ? 'connected' : 'partial'
            }
          : product
      ));

      closeModal();
      alert('Variant connections updated successfully!');
    } catch (error) {
      console.error('Error updating variant connections:', error);
      alert('Failed to update variant connections. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const getConnectionStatusIcon = (product: GroupedProduct) => {
    if (!product.shopifyProductId) {
      return <i className='fas fa-circle text-muted' title='Never connected'></i>;
    }
    
    if (!product.isConnectedToShopify) {
      return <i className='fas fa-pause-circle text-warning' title='Previously connected but now disconnected'></i>;
    }
    
    if (product.connectionStatus === 'partial') {
      return <i className='fas fa-adjust text-info' title='Partially connected'></i>;
    }
    
    return <i className='fas fa-check-circle text-success' title='Fully connected'></i>;
  };

  const closeModal = () => {
    setShowModal(false);
    setShowCreateModal(false);
    setShowAIModal(false);
    setShowFinalModal(false);
    setShowVariantManagementModal(false);
    setSelectedProduct(null);
    setVariantSelections({});
    setSeoKeywords('');
    setAdditionalPrompt('');
    setOptimizedTitle('');
    setOptimizedDescription('');
    setVariantConnectionStatus({});
  };

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return <div className='alert alert-danger ms-3 me-3'>{error}</div>
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers: number[] = [];
    const maxPagesToShow = 5;
    let startPage, endPage;

    if (totalPages <= maxPagesToShow) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const maxPagesBeforeCurrent = Math.floor(maxPagesToShow / 2);
      const maxPagesAfterCurrent = Math.ceil(maxPagesToShow / 2) - 1;
      if (currentPage <= maxPagesBeforeCurrent) {
        startPage = 1;
        endPage = maxPagesToShow;
      } else if (currentPage + maxPagesAfterCurrent >= totalPages) {
        startPage = totalPages - maxPagesToShow + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - maxPagesBeforeCurrent;
        endPage = currentPage + maxPagesAfterCurrent;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <nav>
        <ul className='pagination justify-content-center'>
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(1)}>First</button>
          </li>
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
          </li>
          {startPage > 1 && (
             <li className='page-item disabled'><span className='page-link'>...</span></li>
          )}
          {pageNumbers.map(number => (
            <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
              <button className='page-link' onClick={() => setCurrentPage(number)}>{number}</button>
            </li>
          ))}
           {endPage < totalPages && (
             <li className='page-item disabled'><span className='page-link'>...</span></li>
          )}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className='page-link' onClick={() => setCurrentPage(totalPages)}>Last</button>
          </li>
        </ul>
      </nav>
    );
  };


  return (
    <div className='p-3'>
      {/* Modern Filters */}
      <div className='card mb-4 shadow-sm'>
        <div className='card-body'>
          <div className='row g-3 align-items-center'>
            {/* Search */}
            <div className='col-md-4'>
              <label className='form-label small fw-semibold text-muted mb-1'>Search Products</label>
              <div className='input-group input-group-sm'>
                <span className='input-group-text bg-light border-end-0'>
                  <i className='fas fa-search text-muted'></i>
                </span>
                <input 
                  type='text' 
                  className='form-control border-start-0' 
                  placeholder='Search products or variants...' 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Connection Status Filter */}
            <div className='col-md-2'>
              <label className='form-label small fw-semibold text-muted mb-1'>Connection</label>
              <select 
                className='form-select form-select-sm' 
                value={connectionFilter}
                onChange={(e) => setConnectionFilter(e.target.value as 'all' | 'connected' | 'disconnected')}
              >
                <option value="all">All Products</option>
                <option value="connected">Connected</option>
                <option value="disconnected">Not Connected</option>
              </select>
            </div>

            {/* Brand Filter */}
            <div className='col-md-3'>
              <label className='form-label small fw-semibold text-muted mb-1'>Brand Filter</label>
              <div className='input-group input-group-sm'>
                <span className='input-group-text bg-light border-end-0'>
                  <i className='fas fa-tag text-muted'></i>
                </span>
                <input 
                  type='text' 
                  className='form-control border-start-0' 
                  placeholder='Filter by brand...' 
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                />
              </div>
            </div>

            {/* Legacy Brand Dropdown (keeping for compatibility) */}
            <div className='col-md-1'>
              <label className='form-label small fw-semibold text-muted mb-1'>Brand</label>
              <select 
                className='form-select form-select-sm' 
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
              >
                <option value="">All Brands</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <div className='col-md-2 text-end'>
              <label className='form-label small fw-semibold text-muted mb-1'>Showing</label>
              <div className='small fw-bold'>
                <span className='me-2'>
                  {isCountLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <span>{connectedCount ?? 0}</span>
                  )}
                  <span className='text-muted fw-normal'> conn. / </span>
                  {totalProducts}
                  <span className='text-muted fw-normal'> total</span>
                </span>
              </div>
            </div>
          </div>

          {/* Filter Summary */}
          <div className='row mt-2'>
            <div className='col-12'>
              <div className='d-flex flex-wrap gap-2'>
                {connectionFilter !== 'all' && (
                  <span className='badge bg-info'>
                    {connectionFilter === 'connected' ? 'Connected Only' : 'Not Connected Only'}
                    <button 
                      className='btn-close btn-close-white ms-1' 
                      style={{fontSize: '0.6rem'}}
                      onClick={() => setConnectionFilter('all')}
                    ></button>
                  </span>
                )}
                {brandFilter && (
                  <span className='badge bg-success'>
                    Brand: {brandFilter}
                    <button 
                      className='btn-close btn-close-white ms-1' 
                      style={{fontSize: '0.6rem'}}
                      onClick={() => setBrandFilter('')}
                    ></button>
                  </span>
                )}
                {selectedBrand && (
                  <span className='badge bg-warning'>
                    Category: {selectedBrand}
                    <button 
                      className='btn-close btn-close-white ms-1' 
                      style={{fontSize: '0.6rem'}}
                      onClick={() => setSelectedBrand('')}
                    ></button>
                  </span>
                )}
                {searchTerm && (
                  <span className='badge bg-secondary'>
                    Search: "{searchTerm}"
                    <button 
                      className='btn-close btn-close-white ms-1' 
                      style={{fontSize: '0.6rem'}}
                      onClick={() => setSearchTerm('')}
                    ></button>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Store Info */}
          <div className='row mt-2'>
            <div className='col-12'>
              <small className='text-muted'>
                <i className='fas fa-store me-1'></i>
                Managing products for <strong>{currentStoreName}</strong>
              </small>
            </div>
          </div>

          {/* Connection Status Legend */}
          <div className='row mt-2'>
            <div className='col-12'>
              <small className='text-muted'>
                <strong>Status Icons:</strong>
                <span className='ms-2 me-3'>
                  <i className='fas fa-circle text-muted me-1'></i>
                  Never connected
                </span>
                <span className='me-3'>
                  <i className='fas fa-pause-circle text-warning me-1'></i>
                  Previously connected
                </span>
                <span className='me-3'>
                  <i className='fas fa-adjust text-info me-1'></i>
                  Partially connected
                </span>
                <span className='me-3'>
                  <i className='fas fa-check-circle text-success me-1'></i>
                  Fully connected
                </span>
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className='row g-4'>
        {inventoryData.length > 0 ? (
          inventoryData.map((product) => (
            <div key={product.productId} className='col-12 col-md-6 col-lg-4 col-xl-2'>
              <div className='card h-100 shadow-sm'>
                {product.parentImageUrl && (
                  <img src={product.parentImageUrl} className='card-img-top' alt={product.productTitle} style={{ maxHeight: '200px', objectFit: 'contain', paddingTop: '10px' }} />
                )}
                <div className='card-body d-flex flex-column'>
                  <h5 className='card-title'>{product.productTitle}</h5>
                  
                  {/* Enhanced Connection Status */}
                  <div className='mb-2 d-flex align-items-center justify-content-between'>
                    <div className='d-flex align-items-center gap-2'>
                      {getConnectionStatusIcon(product)}
                      <span className='small'>
                        {!product.shopifyProductId ? (
                          <span className='text-muted'>Never connected</span>
                        ) : !product.isConnectedToShopify ? (
                          <span className='text-warning'>Previously connected</span>
                        ) : product.connectionStatus === 'partial' ? (
                          <span className='text-info'>
                            {product.connectedVariantsCount || 0}/{product.variants.length} variants
                          </span>
                        ) : (
                          <span className='text-success'>All variants connected</span>
                        )}
                      </span>
                    </div>
                    
                    <div className='d-flex align-items-center'>
                      {/* Manage Button for connected products */}
                      {product.shopifyProductId && (
                        <button 
                          className='btn btn-sm btn-outline-primary btn-icon'
                          onClick={() => handleManageVariants(product)}
                          title='Manage variant connections'
                        >
                          <i className='fas fa-cog'></i>
                        </button>
                      )}

                      {/* Shopify Product Link */}
                      {product.shopifyProductId && (
                          <a href={`https://admin.shopify.com/store/${currentStoreName}/products/${product.shopifyProductId}`}
                              target="_blank" rel="noopener noreferrer"
                              className='btn btn-sm btn-icon' title='View on Shopify'>
                              <img src='/media/logos/shopify-icon.png' alt='Shopify' style={{height: '20px'}}/>
                          </a>
                      )}

                      {/* Kanger Product Link */}
                      {product.productUrl && (
                          <a href={product.productUrl}
                              target="_blank" rel="noopener noreferrer"
                              className='btn btn-sm btn-icon' title='View on KangerWholesale'>
                              <img src='/media/logos/kanger-icon.png' alt='Kanger' style={{height: '20px'}}/>
                          </a>
                      )}
                    </div>
                  </div>

                  {/* Last Sync Info - Show most recent sync */}
                  {product.isConnectedToShopify && product.variants.some(v => v.last_sync_at) && (
                    <div className='mb-2 p-2 bg-light rounded'>
                      <small className=' d-flex align-items-center gap-1'>
                        <i className='fas fa-clock' style={{fontSize: '0.75rem'}}></i>
                        <span>
                          Last sync: {formatTimeAgo(
                            Math.max(...product.variants
                              .filter(v => v.last_sync_at)
                              .map(v => new Date(v.last_sync_at!).getTime()))
                          )}
                        </span>
                      </small>
                    </div>
                  )}

                  <div className='variants-container mt-2 flex-grow-1' style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {product.variants.slice(0, 3).map((variant) => (
                      <div key={variant.attribute_id} className='mb-2 border-bottom pb-1'>
                        <div className='d-flex align-items-center'>
                          {variant.variant_image_url && (
                            <img src={variant.variant_image_url} alt={variant.variant_name} style={{ width: '30px', height: '30px', objectFit: 'contain', marginRight: '8px' }} />
                          )}
                          <div className='flex-grow-1'>
                            <div className='d-flex align-items-center gap-1'>
                              <small className='fw-bold'>{variant.variant_name}</small>
                              {variant.is_connected_to_shopify && (
                                <i className='fas fa-link text-success' style={{fontSize: '0.7rem'}} title='Connected to Shopify'></i>
                              )}
                            </div>
                            <small className='text-muted' style={{fontSize: '0.65rem'}}>SKU: KANGER-{product.productId}-{variant.attribute_id}</small>
                            <div>
                              <span className='badge bg-success me-1' style={{fontSize: '0.7rem'}}>{variant.price_formatted || `$${variant.price_value?.toFixed(2)}`}</span>
                              <span className={`badge ${variant.is_in_stock ? 'bg-info' : 'bg-danger'}`} style={{fontSize: '0.7rem'}}>
                                {variant.is_in_stock ? `${variant.stock ?? 'N/A'}` : 'OOS'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {product.variants.length > 3 && (
                      <small className='text-muted'>+{product.variants.length - 3} more variants</small>
                    )}
                  </div>

                  {/* Single Store Toggle */}
                  <div className='mt-auto pt-2'>
                    <div className='form-check form-switch'>
                      <input 
                        className='form-check-input' 
                        type='checkbox' 
                        role='switch' 
                        id={`store-toggle-${product.productId}`}
                        checked={toggleStates[product.productId] || false}
                        onChange={() => handleToggleChange(product.productId)}
                      />
                      <label className='form-check-label small' htmlFor={`store-toggle-${product.productId}`}>
                        {currentStoreName}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className='col-12'>
            <div className='alert alert-info text-center'>No products match your current filters.</div>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className='mt-4'>
        {renderPagination()}
        <div className='text-center text-muted small mt-2'>
          {totalProducts > 0
            ? `Showing ${startProduct} - ${endProduct} of ${totalProducts} products`
            : 'No products found'}
        </div>
      </div>

      {/* Product Configuration Modal */}
      {showModal && selectedProduct && (
        <div className='modal show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Configure Product: {selectedProduct.productTitle}</h5>
                <button type='button' className='btn-close' onClick={closeModal}></button>
              </div>
              <div className='modal-body'>
                {selectedProduct.parentImageUrl && (
                  <div className='text-center mb-3'>
                    <img src={selectedProduct.parentImageUrl} alt={selectedProduct.productTitle} style={{ maxHeight: '150px', objectFit: 'contain' }} />
                  </div>
                )}
                
                <h6>Select Variants to Connect:</h6>
                <div className='row'>
                  {selectedProduct.variants.map((variant) => (
                    <div key={variant.attribute_id} className='col-md-6 mb-3'>
                      <div className='card'>
                        <div className='card-body p-2'>
                          <div className='form-check'>
                            <input 
                              className='form-check-input' 
                              type='checkbox' 
                              id={`variant-${variant.attribute_id}`}
                              checked={variantSelections[variant.attribute_id] || false}
                              onChange={() => handleVariantSelectionChange(variant.attribute_id)}
                            />
                            <label className='form-check-label' htmlFor={`variant-${variant.attribute_id}`}>
                              <div className='d-flex align-items-center'>
                                {variant.variant_image_url && (
                                  <img src={variant.variant_image_url} alt={variant.variant_name} style={{ width: '40px', height: '40px', objectFit: 'contain', marginRight: '10px' }} />
                                )}
                                <div>
                                  <div className='fw-bold'>{variant.variant_name}</div>
                                  <small className='text-muted' style={{fontSize: '0.75rem'}}>SKU: KANGER-{selectedProduct.productId}-{variant.attribute_id}</small>
                                  <div>
                                    <span className='badge bg-success me-1'>{variant.price_formatted || `$${variant.price_value?.toFixed(2)}`}</span>
                                    <span className={`badge ${variant.is_in_stock ? 'bg-info' : 'bg-danger'}`}>
                                      {variant.is_in_stock ? `Stock: ${variant.stock ?? 'N/A'}` : 'Out of Stock'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={closeModal}>Cancel</button>
                <button 
                  type='button' 
                  className='btn btn-primary' 
                  onClick={handleConnectCreate}
                  disabled={modalLoading || Object.values(variantSelections).every(v => !v)}
                >
                  {modalLoading ? 'Checking...' : 'Connect/Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Processing Modal */}
      {showAIModal && selectedProduct && (
        <div className='modal show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>AI Enhancement for: {selectedProduct.productTitle}</h5>
                <button type='button' className='btn-close' onClick={closeModal}></button>
              </div>
              <div className='modal-body'>
                {selectedProduct.parentImageUrl && (
                  <div className='text-center mb-3'>
                    <img src={selectedProduct.parentImageUrl} alt={selectedProduct.productTitle} style={{ maxHeight: '120px', objectFit: 'contain' }} />
                  </div>
                )}
                
                <div className='mb-3'>
                  <strong>Selected Variants:</strong> {Object.values(variantSelections).filter(Boolean).length} of {selectedProduct.variants.length}
                </div>
                
                <div className='mb-3'>
                  <label className='form-label'>SEO Keywords (comma-separated)</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='vape, e-cigarette, mod, tank...'
                    value={seoKeywords}
                    onChange={(e) => setSeoKeywords(e.target.value)}
                  />
                  <small className='form-text text-muted'>Keywords to optimize the product description for SEO</small>
                </div>
                
                <div className='mb-3'>
                  <label className='form-label'>Additional Information (optional)</label>
                  <textarea
                    className='form-control'
                    rows={4}
                    placeholder='Any additional information about the product, features, benefits, etc.'
                    value={additionalPrompt}
                    onChange={(e) => setAdditionalPrompt(e.target.value)}
                  />
                  <small className='form-text text-muted'>This will help AI create a better product description</small>
                </div>
                
                <div className='alert alert-info'>
                  <strong>AI will:</strong>
                  <ul className='mb-0 mt-2'>
                    <li>Optimize the product title for SEO</li>
                    <li>Create a compelling product description</li>
                    <li>Include variant information and pricing</li>
                    <li>Add relevant keywords for better search visibility</li>
                  </ul>
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={closeModal}>Cancel</button>
                <button 
                  type='button' 
                  className='btn btn-primary' 
                  onClick={handleProcessWithAI}
                  disabled={aiProcessing}
                >
                  {aiProcessing ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-1' role='status'></span>
                      Processing with AI...
                    </>
                  ) : (
                    'Process with AI'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final Product Confirmation Modal */}
      {showFinalModal && selectedProduct && (
        <div className='modal show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-xl'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Final Review & Create Product</h5>
                <button type='button' className='btn-close' onClick={closeModal}></button>
              </div>
              <div className='modal-body'>
                <div className='row'>
                  <div className='col-md-6'>
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>Optimized Title (Editable)</label>
                      <input
                        type='text'
                        className='form-control'
                        value={optimizedTitle}
                        onChange={(e) => setOptimizedTitle(e.target.value)}
                      />
                    </div>
                    
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>Optimized Description (Editable)</label>
                      <textarea
                        className='form-control'
                        rows={12}
                        value={optimizedDescription}
                        onChange={(e) => setOptimizedDescription(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className='col-md-6'>
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>HTML Preview</label>
                      <div
                        className='border p-3 bg-light'
                        style={{minHeight: 200, maxHeight: 400, overflowY: 'auto'}}
                        dangerouslySetInnerHTML={{__html: optimizedDescription}}
                      />
                    </div>
                    
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>Product Details</label>
                      <div className='card'>
                        <div className='card-body p-2'>
                          <p><strong>Original Title:</strong> {selectedProduct.productTitle}</p>
                          <p><strong>Brand:</strong> {selectedProduct.variants[0]?.brand_name || 'GeekVape'}</p>
                          <p><strong>Selected Variants:</strong> {Object.values(variantSelections).filter(Boolean).length}</p>
                          <p><strong>Store:</strong> {currentStoreName}</p>
                          <p><strong>Status:</strong> Will be created as DRAFT</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>Selected Variants</label>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {selectedProduct.variants.filter(v => variantSelections[v.attribute_id]).map((variant) => (
                          <div key={variant.attribute_id} className='card mb-2'>
                            <div className='card-body p-2'>
                              <div className='d-flex align-items-center'>
                                {variant.variant_image_url && (
                                  <img src={variant.variant_image_url} alt={variant.variant_name} style={{ width: '40px', height: '40px', objectFit: 'contain', marginRight: '10px' }} />
                                )}
                                <div className='flex-grow-1'>
                                  <div className='fw-bold'>{variant.variant_name}</div>
                                  <small className='text-muted' style={{fontSize: '0.75rem'}}>SKU: KANGER-{selectedProduct.productId}-{variant.attribute_id}</small>
                                  <div>
                                    <span className='badge bg-success me-1'>{variant.price_formatted || `$${variant.price_value?.toFixed(2)}`}</span>
                                    <span className={`badge ${variant.is_in_stock ? 'bg-info' : 'bg-danger'}`}>
                                      {variant.is_in_stock ? `Stock: ${variant.stock ?? 'N/A'}` : 'Out of Stock'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {selectedProduct.parentImageUrl && (
                      <div className='mb-3'>
                        <label className='form-label fw-bold'>Product Image</label>
                        <div className='text-center'>
                          <img src={selectedProduct.parentImageUrl} alt={selectedProduct.productTitle} style={{ maxHeight: '150px', objectFit: 'contain' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className='alert alert-warning'>
                  <strong>Note:</strong> The product will be created as a DRAFT in your Shopify store. You can review and publish it from your Shopify admin.
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={() => setShowFinalModal(false)}>Back to Edit</button>
                <button 
                  type='button' 
                  className='btn btn-success' 
                  onClick={handleCreateNewProduct}
                  disabled={modalLoading || !optimizedTitle.trim() || !optimizedDescription.trim()}
                >
                  {modalLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-1' role='status'></span>
                      Creating Product...
                    </>
                  ) : (
                    'Create Product in Shopify'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Variant Management Modal */}
      {showVariantManagementModal && selectedProduct && (
        <div className='modal show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>
                  <i className='fas fa-cog me-1'></i>
                  Manage Variants: {selectedProduct.productTitle}
                </h5>
                <button type='button' className='btn-close' onClick={closeModal}></button>
              </div>
              <div className='modal-body'>
                {selectedProduct.parentImageUrl && (
                  <div className='text-center mb-3'>
                    <img src={selectedProduct.parentImageUrl} alt={selectedProduct.productTitle} style={{ maxHeight: '120px', objectFit: 'contain' }} />
                  </div>
                )}

                {/* Overall Product Sync Info */}
                {selectedProduct.variants.some(v => v.last_sync_at) && (
                  <div className='alert alert-light border mb-3'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <div>
                        <strong className='text-primary'>
                          <i className='fas fa-info-circle me-1'></i>
                          Product Sync Status
                        </strong>
                      </div>
                      <div className='text-end'>
                        <small className='text-muted'>
                          <i className='fas fa-clock me-1'></i>
                          Most recent sync: {formatTimeAgo(
                            Math.max(...selectedProduct.variants
                              .filter(v => v.last_sync_at)
                              .map(v => new Date(v.last_sync_at!).getTime()))
                          )}
                        </small>
                      </div>
                    </div>
                    {(() => {
                      const syncedCount = selectedProduct.variants.filter(v => v.last_sync_at).length;
                      const totalCount = selectedProduct.variants.length;
                      return syncedCount < totalCount && (
                        <div className='mt-2'>
                          <small className='text-warning'>
                            <i className='fas fa-exclamation-triangle me-1'></i>
                            {totalCount - syncedCount} variant{totalCount - syncedCount > 1 ? 's have' : ' has'} never been synced
                          </small>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className='mb-3'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <label className='form-label fw-bold mb-0'>Variant Connections</label>
                    <div className='btn-group btn-group-sm'>
                      <button 
                        className='btn btn-outline-success'
                        onClick={() => {
                          const newStatus: VariantConnectionStatus = {};
                          selectedProduct.variants.forEach(v => {
                            newStatus[v.attribute_id] = true;
                          });
                          setVariantConnectionStatus(newStatus);
                        }}
                      >
                        <i className='fas fa-check-double me-1'></i>
                        Connect All
                      </button>
                      <button 
                        className='btn btn-outline-danger'
                        onClick={() => {
                          const newStatus: VariantConnectionStatus = {};
                          selectedProduct.variants.forEach(v => {
                            newStatus[v.attribute_id] = false;
                          });
                          setVariantConnectionStatus(newStatus);
                        }}
                      >
                        <i className='fas fa-times me-1'></i>
                        Disconnect All
                      </button>
                    </div>
                  </div>
                  
                  <div className='row'>
                    {selectedProduct.variants.map((variant) => (
                      <div key={variant.attribute_id} className='col-md-6 mb-3'>
                        <div className={`card ${variantConnectionStatus[variant.attribute_id] ? 'border-success' : 'border-secondary'}`}>
                          <div className='card-body p-3'>
                            <div className='form-check form-switch'>
                              <input 
                                className='form-check-input' 
                                type='checkbox' 
                                id={`variant-manage-${variant.attribute_id}`}
                                checked={variantConnectionStatus[variant.attribute_id] || false}
                                onChange={() => handleVariantConnectionChange(variant.attribute_id)}
                              />
                              <label className='form-check-label fw-bold' htmlFor={`variant-manage-${variant.attribute_id}`}>
                                {variant.variant_name}
                              </label>
                            </div>
                            
                            <div className='d-flex align-items-center mt-2'>
                              {variant.variant_image_url && (
                                <img src={variant.variant_image_url} alt={variant.variant_name} style={{ width: '40px', height: '40px', objectFit: 'contain', marginRight: '10px' }} />
                              )}
                              <div className='flex-grow-1'>
                                <div className='d-flex gap-2 mb-1'>
                                  <span className='badge bg-success'>{variant.price_formatted || `$${variant.price_value?.toFixed(2)}`}</span>
                                  <span className={`badge ${variant.is_in_stock ? 'bg-info' : 'bg-danger'}`}>
                                    {variant.is_in_stock ? `Stock: ${variant.stock ?? 'N/A'}` : 'Out of Stock'}
                                  </span>
                                </div>
                                <small className='text-muted'>
                                  SKU: KANGER-{selectedProduct.productId}-{variant.attribute_id}
                                </small>
                                <div className='d-flex align-items-center mt-1'>
                                  {variant.is_connected_to_shopify && (
                                      <small className='text-success'>
                                        <i className='fas fa-link me-1'></i>
                                        Currently connected
                                      </small>
                                  )}
                                  {variant.is_connected_to_shopify && variant.shopify_variant_id && selectedProduct.shopifyProductId && (
                                    <a
                                      href={`https://admin.shopify.com/store/${currentStoreName}/products/${selectedProduct.shopifyProductId}/variants/${variant.shopify_variant_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className='ms-2'
                                      title='View variant on Shopify'
                                    >
                                      <img src='/media/logos/shopify-icon.png' alt='Shopify' style={{ height: '16px', width: '16px' }} />
                                    </a>
                                  )}
                                </div>
                                {/* Last Sync Time for Individual Variant */}
                                {variant.last_sync_at && (
                                  <div className='mt-1'>
                                    <small className='text-muted' style={{fontSize: '0.7rem'}}>
                                      <i className='fas fa-clock me-1' style={{fontSize: '0.65rem'}}></i>
                                      Last sync: {formatTimeAgo(variant.last_sync_at)}
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className='alert alert-info'>
                  <i className='fas fa-info-circle me-1'></i>
                  <strong>Note:</strong> Changes will update which variants are actively connected to your Shopify store. 
                  Disconnected variants will remain in Shopify but won't sync inventory or pricing.
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={closeModal}>Cancel</button>
                <button 
                  type='button' 
                  className='btn btn-primary' 
                  onClick={handleSaveVariantConnections}
                  disabled={modalLoading}
                >
                  {modalLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-1' role='status'></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-save me-1'></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Interface for sync response (matching the modal interface)
interface SyncResponse {
  message: string;
  summary: {
    productsProcessed: number;
    variantsUpdated: number;
    shopifyUpdates: number;
    databaseUpdates: number;
    storesAffected: string[];
    totalErrors: number;
    scrapeErrors: number;
    shopifyErrors: number;
    databaseErrors: number;
  };
  storeBreakdown: Record<string, any>;
  errors: {
    scrapeErrors: string[];
    shopifyErrors: string[];
    databaseErrors: string[];
  };
}

// Interface for SKU sync response
interface SkuSyncResponse {
  message: string;
  summary: {
    storesProcessed: string[];
    totalNewConnections: number;
    connectionsMade: any[]; // Define more specific type if needed
  };
}

export const KangerWholesaleWrapper: FC = () => {
  const {currentUser} = useAuth()
  const [isUpdatingInventory, setIsUpdatingInventory] = useState<boolean>(false)
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  
  // Single Product Scrape Modal
  const [showSingleProductModal, setShowSingleProductModal] = useState<boolean>(false)

  // Sync modal states
  const [showSyncModal, setShowSyncModal] = useState<boolean>(false)
  const [isSyncing, setIsSyncing] = useState<boolean>(false)
  const [syncData, setSyncData] = useState<SyncResponse | undefined>(undefined)
  const [syncJobId, setSyncJobId] = useState<string | null>(null)
  const [syncLogs, setSyncLogs] = useState<string[]>([])
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // SKU Sync states
  const [showSkuSyncModal, setShowSkuSyncModal] = useState<boolean>(false)
  const [isSkuSyncing, setIsSkuSyncing] = useState<boolean>(false)
  const [skuSyncData, setSkuSyncData] = useState<SkuSyncResponse | undefined>(undefined)
  const [skuSyncJobId, setSkuSyncJobId] = useState<string | null>(null)
  const [skuSyncLogs, setSkuSyncLogs] = useState<string[]>([])
  const [skuSyncError, setSkuSyncError] = useState<string | undefined>(undefined)
  const skuPollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const [isGeneratingScript, setIsGeneratingScript] = useState<boolean>(false); // New state for script generation

  const handleUpdateInventory = async () => {
    setIsUpdatingInventory(true);
    try {
      console.log('Starting inventory update...');
      
      // Hit the scraping endpoint
      const response = await axios.get(`${API_URL}/api/kangerwholesale/inventory`);
      
      console.log('Inventory update response:', response.data);
      
      setLastUpdateTime(new Date());
      setRefreshTrigger(prev => prev + 1); // Trigger refresh in child component
      alert(`Inventory updated successfully! ${response.data.summary?.variantsScraped || 'Unknown'} variants processed.`);
      
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('Failed to update inventory. Please try again.');
    } finally {
      setIsUpdatingInventory(false);
    }
  };

  const handleGenerateScript = async (type: 'add' | 'clear') => {
    setIsGeneratingScript(true);
    const storeName = currentUser?.decoded?.current_shopify_store || '';
    if (!storeName) {
      alert('No Shopify store selected.');
      setIsGeneratingScript(false);
      return;
    }
  
    let url = '';
    if (type === 'add') {
      url = `${API_URL}/api/fulfillment/generate-kanger-script?storeName=${encodeURIComponent(storeName)}`;
    } else {
      url = `${API_URL}/api/fulfillment/generate-clear-cart-script`;
    }
  
    try {
      const response = await axios.get(url, { responseType: 'text' });
      const scriptText = response.data;
  
      if (scriptText) {
        await navigator.clipboard.writeText(scriptText);
        alert(`The script to '${type.toUpperCase()}' items has been copied to your clipboard. Paste it in the Kanger console.`);
      } else {
        alert('Received an empty script from the server. There might be nothing to do.');
      }
    } catch (error) {
      console.error(`Error generating ${type} script:`, error);
      alert(`Failed to generate the script. Please check the console for details.`);
    } finally {
      setIsGeneratingScript(false);
    }
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const pollSyncStatus = async (jobId: string) => {
    try {
      const response = await axios.get(`${API_URL}/api/kangerwholesale/sync-status/${jobId}`);
      const { status, logs, result, error } = response.data;

      setSyncLogs(logs);

      if (status === 'completed') {
        stopPolling();
        setIsSyncing(false);
        setSyncData(result);
        setRefreshTrigger(prev => prev + 1);
      } else if (status === 'failed') {
        stopPolling();
        setIsSyncing(false);
        setSyncData({
          message: `Sync failed: ${error}`,
          summary: { totalErrors: 1, productsProcessed: 0, variantsUpdated: 0, shopifyUpdates: 0, databaseUpdates: 0, storesAffected: [], scrapeErrors: 0, shopifyErrors: 0, databaseErrors: 1 },
          storeBreakdown: {},
          errors: { scrapeErrors: [], shopifyErrors: [], databaseErrors: [error] }
        });
      }
    } catch (pollError) {
      console.error('Error polling sync status:', pollError);
      stopPolling();
      setIsSyncing(false);
      setSyncData({
        message: 'Failed to get sync status. Please check the server.',
        summary: { totalErrors: 1, productsProcessed: 0, variantsUpdated: 0, shopifyUpdates: 0, databaseUpdates: 0, storesAffected: [], scrapeErrors: 0, shopifyErrors: 0, databaseErrors: 1 },
        storeBreakdown: {},
        errors: { scrapeErrors: [], shopifyErrors: [], databaseErrors: ['Polling failed'] }
      });
    }
  };

  const handleSyncInventory = async (storeName?: string) => {
    setShowSyncModal(true);
    setIsSyncing(true);
    setSyncData(undefined);
    setSyncLogs([`Initiating ${storeName ? `sync for ${storeName}` : 'sync for all stores'}...`]);

    try {
      // 1. Start the sync job
      const requestBody = storeName ? { storeName } : {};
      const response = await axios.post(`${API_URL}/api/kangerwholesale/start-sync`, requestBody);
      const { jobId } = response.data;
      setSyncJobId(jobId);

      // 2. Start polling for status
      pollingIntervalRef.current = setInterval(() => {
        pollSyncStatus(jobId);
      }, 5000); // Poll every 5 seconds

    } catch (error) {
      console.error('Error starting inventory sync:', error);
      setIsSyncing(false);
      setSyncData({
        message: 'Failed to start the sync process.',
        summary: { totalErrors: 1, productsProcessed: 0, variantsUpdated: 0, shopifyUpdates: 0, databaseUpdates: 0, storesAffected: [], scrapeErrors: 0, shopifyErrors: 0, databaseErrors: 1 },
        storeBreakdown: {},
        errors: { scrapeErrors: [], shopifyErrors: [], databaseErrors: [error instanceof Error ? error.message : 'Unknown error'] }
      });
    }
  };

  const handleCloseSyncModal = () => {
    setShowSyncModal(false);
    stopPolling();
    // Do not clear data, so user can see last result
    // setSyncData(undefined);
    // setIsSyncing(false);
    setSyncJobId(null);
  };

  const handleRefreshData = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  const stopSkuPolling = () => {
    if (skuPollingIntervalRef.current) {
      clearInterval(skuPollingIntervalRef.current);
      skuPollingIntervalRef.current = null;
    }
  };

  const pollSkuSyncStatus = async (jobId: string) => {
    try {
      const response = await axios.get(`${API_URL}/api/kangerwholesale/sku-sync-status/${jobId}`);
      const { status, logs, result, error } = response.data;

      setSkuSyncLogs(logs);

      if (status === 'completed') {
        stopSkuPolling();
        setIsSkuSyncing(false);
        setSkuSyncData(result);
        setRefreshTrigger(prev => prev + 1); // Refresh data grid to show new connections
      } else if (status === 'failed') {
        stopSkuPolling();
        setIsSkuSyncing(false);
        setSkuSyncError(error);
      }
    } catch (pollError) {
      console.error('Error polling SKU sync status:', pollError);
      stopSkuPolling();
      setIsSkuSyncing(false);
      setSkuSyncError('Failed to get SKU sync status. Please check the server.');
    }
  };

  const handleSkuSync = async () => {
    setShowSkuSyncModal(true);
    setIsSkuSyncing(true);
    setSkuSyncData(undefined);
    setSkuSyncError(undefined);
    setSkuSyncLogs(['Initiating SKU sync process...']);

    try {
      const response = await axios.post(`${API_URL}/api/kangerwholesale/start-sku-sync`, {});
      const { jobId } = response.data;
      setSkuSyncJobId(jobId);

      skuPollingIntervalRef.current = setInterval(() => {
        pollSkuSyncStatus(jobId);
      }, 5000); // Poll every 5 seconds
    } catch (error) {
      console.error('Error starting SKU sync:', error);
      setIsSkuSyncing(false);
      setSkuSyncError(error instanceof Error ? error.message : 'Failed to start the SKU sync process.');
    }
  };

  const handleCloseSkuSyncModal = () => {
    setShowSkuSyncModal(false);
    stopSkuPolling();
    setSkuSyncJobId(null);
  };

  useEffect(() => {
    // Cleanup interval on component unmount
    return () => {
      stopPolling();
      stopSkuPolling();
    };
  }, []);

  return (
    <>
      {/* Dark Header with Store Info */}
      <div className='border-bottom border-secondary py-3 px-3' style={{backgroundColor: '#0e1425'}}>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center'>
            <h2 className='mb-0 me-3 fw-bold' style={{color: '#ffffff'}}>KangerWholesale Dashboard</h2>
            <div className='badge bg-primary text-white border-0 px-3 py-2'>
              <i className='fas fa-store me-1'></i>
              {currentUser?.decoded?.current_shopify_store || 'No Store Selected'}
            </div>
          </div>
          <div>
            {lastUpdateTime && (
              <small className='text-light'>
                <i className='fas fa-clock me-1'></i>
                Last updated: {lastUpdateTime.toLocaleString()}
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Compact Action Toolbar */}
      <div className='py-3 px-3'>
        <div className='d-flex flex-wrap gap-4 justify-content-center'>
          
          {/* Single Product Scrape */}
          <div className='d-flex align-items-center'>
            <button
              className='btn btn-outline-info d-flex align-items-center'
              onClick={() => setShowSingleProductModal(true)}
              disabled={isUpdatingInventory}
              style={{fontSize: '0.8rem', padding: '0.25rem 0.5rem'}}
            >
              <i className='fas fa-search-plus me-1'></i>
              Single Product Scrape
            </button>
            <div className='dropdown' style={{marginLeft: '0.25rem'}}>
              <button
                className='btn btn-link btn-sm p-1 text-muted'
                type='button'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                title='Learn more'
              >
                <i className='fas fa-question-circle'></i>
              </button>
              <div className='dropdown-menu dropdown-menu-end bg-dark border-secondary p-3' style={{ maxWidth: '300px' }}>
                <div className='small text-light'>
                  <strong className='text-info'>Single Product Scrape</strong>
                  <hr className='my-2 border-secondary'/>
                  <p className='mb-0 text-light'>Refreshes one specific product using its Kanger URL. Updates inventory levels, pricing, and product details in your database.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Full Catalog Scrape */}
          <div className='d-flex align-items-center'>
            <button 
              className='btn btn-outline-primary d-flex align-items-center'
              onClick={handleUpdateInventory}
              disabled={isUpdatingInventory}
              style={{fontSize: '0.8rem', padding: '0.25rem 0.5rem'}}
            >
              {isUpdatingInventory ? (
                <>
                  <span className='spinner-border spinner-border-sm me-1' role='status'></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className='fas fa-sync-alt me-1'></i>
                  Full Catalog Scrape
                </>
              )}
            </button>
            <div className='dropdown' style={{marginLeft: '0.25rem'}}>
              <button
                className='btn btn-link btn-sm p-1 text-muted'
                type='button'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                title='Learn more'
              >
                <i className='fas fa-question-circle'></i>
              </button>
              <div className='dropdown-menu dropdown-menu-end bg-dark border-secondary p-3' style={{ maxWidth: '300px' }}>
                <div className='small text-light'>
                  <strong className='text-primary'>Full Catalog Scrape</strong>
                  <hr className='my-2 border-secondary'/>
                  <p className='mb-0 text-light'>Updates your entire product catalog with current Kanger inventory, pricing, and availability. Processes all brands (up to 400 products each). Use weekly due to rate limits.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sync Connected Inventory */}
          <div className='d-flex align-items-center'>
            <div className='dropdown'>
              <button
                className='btn btn-outline-success dropdown-toggle d-flex align-items-center'
                type='button'
                id='syncDropdown'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                disabled={isSyncing}
                style={{fontSize: '0.8rem', padding: '0.25rem 0.5rem'}}
              >
                {isSyncing ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-1' role='status'></span>
                    Syncing...
                  </>
                ) : (
                  <>
                    <i className='fas fa-cloud-upload-alt me-1'></i>
                    Sync Connected Inventory
                  </>
                )}
              </button>
              <ul className='dropdown-menu bg-dark border-secondary shadow' aria-labelledby='syncDropdown'>
                <li>
                  <a 
                    className='dropdown-item py-2 text-light' 
                    href='#' 
                    onClick={(e) => { e.preventDefault(); handleSyncInventory(currentUser?.decoded?.current_shopify_store || ''); }}
                  >
                    <i className='fas fa-store me-1 text-info'></i>
                    Current Store Only
                    <small className='d-block text-muted'>({currentUser?.decoded?.current_shopify_store || 'None'})</small>
                  </a>
                </li>
                <li><hr className='dropdown-divider border-secondary'/></li>
                <li>
                  <a 
                    className='dropdown-item py-2 text-light' 
                    href='#' 
                    onClick={(e) => { e.preventDefault(); handleSyncInventory(); }}
                  >
                    <i className='fas fa-globe me-1 text-success'></i>
                    All Connected Stores
                    <small className='d-block text-muted'>Sync all stores at once</small>
                  </a>
                </li>
              </ul>
            </div>
            <div className='dropdown' style={{marginLeft: '0.25rem'}}>
              <button
                className='btn btn-link btn-sm p-1 text-muted'
                type='button'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                title='Learn more'
              >
                <i className='fas fa-question-circle'></i>
              </button>
              <div className='dropdown-menu dropdown-menu-end bg-dark border-secondary p-3' style={{ maxWidth: '300px' }}>
                <div className='small text-light'>
                  <strong className='text-success'>Sync Connected Inventory</strong>
                  <hr className='my-2 border-secondary'/>
                  <p className='mb-0 text-light'>Updates Shopify stock levels for products already linked to Kanger. Ensures your store inventory matches current supplier availability.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Link Shopify SKUs */}
          <div className='d-flex align-items-center'>
            <button 
              className='btn btn-outline-warning d-flex align-items-center'
              onClick={handleSkuSync}
              disabled={isSkuSyncing}
              style={{fontSize: '0.8rem', padding: '0.25rem 0.5rem'}}
            >
              {isSkuSyncing ? (
                <>
                  <span className='spinner-border spinner-border-sm me-1' role='status'></span>
                  Linking...
                </>
              ) : (
                <>
                  <i className='fas fa-link me-1'></i>
                  Link Shopify SKUs
                </>
              )}
            </button>
            <div className='dropdown' style={{marginLeft: '0.25rem'}}>
              <button
                className='btn btn-link btn-sm p-1 text-muted'
                type='button'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                title='Learn more'
              >
                <i className='fas fa-question-circle'></i>
              </button>
              <div className='dropdown-menu dropdown-menu-end bg-dark border-secondary p-3' style={{ maxWidth: '300px' }}>
                <div className='small text-light'>
                  <strong className='text-warning'>Link Shopify SKUs</strong>
                  <hr className='my-2 border-secondary'/>
                  <p className='mb-0 text-light'>Finds Shopify products with Kanger SKUs (format: kanger_[product_id]_[variant_id]) and links them to your supplier database for automatic syncing.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Fulfillment Actions */}
          <div className='d-flex align-items-center'>
            <div className='dropdown'>
              <button
                className='btn btn-primary dropdown-toggle d-flex align-items-center'
                type='button'
                id='fulfillmentActionsDropdown'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                disabled={isGeneratingScript}
                style={{fontSize: '0.8rem', padding: '0.25rem 0.5rem'}}
              >
                {isGeneratingScript ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-1' role='status'></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className='fas fa-shopping-cart me-1'></i>
                    Fulfillment Actions
                  </>
                )}
              </button>
              <ul className='dropdown-menu bg-dark border-secondary shadow' aria-labelledby='fulfillmentActionsDropdown'>
                <li>
                  <a 
                    className='dropdown-item py-2 text-light' 
                    href='#' 
                    onClick={(e) => { e.preventDefault(); handleGenerateScript('add'); }}
                  >
                    <i className='fas fa-plus-circle me-1 text-success'></i>
                    Generate Add to Cart Script
                    <small className='d-block text-muted'>Copy script to add items to Kanger cart</small>
                  </a>
                </li>
                <li><hr className='dropdown-divider border-secondary'/></li>
                <li>
                  <a 
                    className='dropdown-item py-2 text-light' 
                    href='#' 
                    onClick={(e) => { e.preventDefault(); handleGenerateScript('clear'); }}
                  >
                    <i className='fas fa-trash-alt me-1 text-danger'></i>
                    Generate Clear Cart Script
                    <small className='d-block text-muted'>Copy script to empty Kanger cart</small>
                  </a>
                </li>
              </ul>
            </div>
            <div className='dropdown' style={{marginLeft: '0.25rem'}}>
              <button
                className='btn btn-link btn-sm p-1 text-muted'
                type='button'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                title='Learn more'
              >
                <i className='fas fa-question-circle'></i>
              </button>
              <div className='dropdown-menu dropdown-menu-end bg-dark border-secondary p-3' style={{ maxWidth: '300px' }}>
                <div className='small text-light'>
                  <strong className='text-primary'>Fulfillment Actions</strong>
                  <hr className='my-2 border-secondary'/>
                  <p className='mb-0 text-light'>Generate JavaScript scripts to automate cart management on the Kanger website. Use these scripts in your browser console to add or clear items from your cart.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <KangerWholesalePage 
        onUpdateInventory={handleUpdateInventory}
        isUpdatingInventory={isUpdatingInventory}
        lastUpdateTime={lastUpdateTime}
        refreshData={handleUpdateInventory}
        refreshTrigger={refreshTrigger}
      />
      
      {/* Modals */}
      <KangerWholesaleSingleProductModal
        show={showSingleProductModal}
        onClose={() => setShowSingleProductModal(false)}
        onScrapeComplete={handleRefreshData}
      />

      {/* Sync Results Modal */}
      <KangerWholesaleUpdateModal 
        show={showSyncModal}
        onClose={handleCloseSyncModal}
        syncData={syncData}
        loading={isSyncing}
        syncLogs={syncLogs}
      />

      {/* SKU Sync Modal */}
      <KangerSkuSyncModal
        show={showSkuSyncModal}
        onClose={handleCloseSkuSyncModal}
        syncData={skuSyncData}
        loading={isSkuSyncing}
        syncLogs={skuSyncLogs}
        error={skuSyncError}
      />
    </>
  )
} 